/**
 * Border segments — the addressable surface of a kingdom.
 *
 * A segment is exactly one edge: an owned hex plus the direction index (0-5) of
 * the neighbour being pushed into. That identity scheme is open question #2 in
 * the roadmap, resolved in `shared/src/rules.ts` (`borderSegmentId`) because an
 * id keyed off a *run* of edges is invalidated the moment a single hex in the run
 * flips owner — which is precisely when the player is dragging a surge across it.
 *
 * Segments are recomputed on demand rather than cached. The roadmap's Stage 3.2
 * asked for incremental recomputation on ownership change; at 40x40 with six
 * kingdoms a full recompute costs a small fraction of a 100ms tick (measured in
 * `borders.test.ts`), and a cache keyed off ownership changes has to be
 * invalidated from four different places in the simulation. An invisible stale
 * border is a far more expensive bug than the microseconds saved, so the cache is
 * deliberately not built until a profile demands it.
 */
import { HEX_DIRECTION_COUNT, borderSegmentId, hexKey, hexNeighbor, parseBorderSegmentId, } from "kngdm-shared";
/**
 * Every edge where `kingdomId` touches land it does not own.
 *
 * Edges pointing off the map are not segments: there is nothing there to push
 * into, so surging at one would silently swallow troops.
 */
export function computeBorderSegments(state, kingdomId) {
    const kingdom = state.kingdoms.get(kingdomId);
    if (kingdom === undefined)
        return [];
    const segments = [];
    for (const key of kingdom.hexes) {
        const owned = state.hexes.get(key);
        // `hexes` is the authoritative territory record, but ownership is written in
        // two places on a capture (the set and the hex). Trusting the hex means a
        // desync between them can never invent a segment for land we don't hold.
        if (owned === undefined || owned.owner !== kingdomId)
            continue;
        for (let direction = 0; direction < HEX_DIRECTION_COUNT; direction++) {
            const to = hexNeighbor(owned.coord, direction);
            const neighbour = state.hexes.get(hexKey(to));
            if (neighbour === undefined)
                continue;
            if (neighbour.owner === kingdomId)
                continue;
            segments.push({
                id: borderSegmentId(owned.coord, direction),
                from: owned.coord,
                to,
                direction,
                ownerId: kingdomId,
                targetOwnerId: neighbour.owner,
            });
        }
    }
    return segments;
}
/**
 * Whether `kingdomId` may surge along `segmentId`, right now.
 *
 * Cheaper than `computeBorderSegments(...).some(...)` and, more importantly,
 * total: a malformed id from a client is a `false`, never a thrown error inside
 * a tick.
 */
export function isValidSurgeTarget(state, kingdomId, segmentId) {
    let parsed;
    try {
        parsed = parseBorderSegmentId(segmentId);
    }
    catch {
        return false;
    }
    const owned = state.hexes.get(hexKey(parsed.hex));
    if (owned === undefined || owned.owner !== kingdomId)
        return false;
    const neighbour = state.hexes.get(hexKey(hexNeighbor(parsed.hex, parsed.direction)));
    if (neighbour === undefined)
        return false;
    return neighbour.owner !== kingdomId;
}
//# sourceMappingURL=borders.js.map